import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fetchReplaceProjectEpisode } from '@/service/api/project';
import ImageUploadBox from '@/components/common/ImageUploadBox';

interface ReplaceEpisodeModalProps {
  open: boolean;
  projectId: string;
  epNo: number | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export default function ReplaceEpisodeModal({
  open,
  projectId,
  epNo,
  onClose,
  onSuccess,
  onError,
}: ReplaceEpisodeModalProps) {
  const [replaceAssetId, setReplaceAssetId] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);

  useEffect(() => {
    if (!open) {
      setReplaceAssetId('');
    }
  }, [open]);

  if (!open || epNo === null) return null;

  /** 提交替换单集文件 */
  const handleReplaceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replaceAssetId.trim()) return;
    setIsReplacing(true);
    try {
      await fetchReplaceProjectEpisode(projectId, epNo, {
        upload_asset_id: replaceAssetId.trim(),
      });
      onSuccess(`第 ${epNo} 集文件替换请求已提交！`);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : '替换剧集文件失败');
    } finally {
      setIsReplacing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form
        onSubmit={handleReplaceSubmit}
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150"
      >
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
          <h4 className="font-bold text-foreground-900 text-base">替换第 {epNo} 集视频文件</h4>
          <button
            type="button"
            onClick={onClose}
            className="text-foreground-400 hover:text-foreground-700 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <p className="text-xs text-foreground-500 mb-4">替换后系统将生成新的文件版本并重新进入审核队列</p>

        <div className="mb-4">
          <ImageUploadBox
            label="视频文件"
            required
            value={replaceAssetId}
            onChange={(assetId) => setReplaceAssetId(assetId)}
            scene="APPLICATION"
            policy="uploader-only"
            isVideo={true}
            accept="video/*,.mp4,.mov,.mkv"
            placeholder="点击或拖拽上传新视频文件"
            subPlaceholder="支持 MP4, MOV, MKV 常见视频格式"
          />
        </div>

        {replaceAssetId && (
          <div className="mb-4 p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-[11px] text-foreground-600 flex items-center justify-between">
            <span className="text-foreground-400">资源 ID:</span>
            <span className="font-mono font-medium text-foreground-800">{replaceAssetId}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-100 text-foreground-700 text-xs hover:bg-gray-200 transition-colors"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isReplacing || !replaceAssetId.trim()}
            className="px-4 py-2 rounded-xl bg-primary-500 text-foreground-950 font-medium text-xs hover:bg-primary-400 disabled:opacity-50 flex items-center gap-1 transition-colors"
          >
            {isReplacing && <i className="ri-loader-4-line animate-spin"></i>}
            确认提交替换
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
